import {
  codemirrorWorkspaceServiceModule,
  WORKSPACE_STATUS,
  WORKSPACE_STATUS_LABELS,
} from '../codemirror-workspace-service/codemirror-workspace-service';
import { storageServiceModule } from '../storage-service/storage-service';
import { smartIdeDialogComponentModule } from '../../components/smart-ide-dialog/smart-ide-dialog';
import { trackerServiceModule } from '../tracker-service/tracker-service';
import { appAlertComponentModule } from '../../components/app-alert/app-alert';

// stores user preference on smart IDE enabled in session, needs challenge ID appended
export const SMART_IDE_DISABLED_PREFERENCE_KEY = 'QualifiedSmartIdeDisabled-';

// Shown when SmartIDE gives up connecting. It leads with the fact that the
// assessment itself is unaffected, because the reported failure mode was a
// candidate who believed the whole editor had frozen and stopped working.
export const SMART_IDE_CONNECTION_FAILED_MESSAGE =
  'SmartIDE could not connect, so code completion and inspection are ' +
  'unavailable. Your code and the rest of the challenge are unaffected — you ' +
  'can keep working, or restart SmartIDE to try again.';

export const SMART_IDE_RESTART_LINK_TITLE = 'Restart SmartIDE';

export const multiFileChallengeServiceSmartIdeModule = angular
  .module('Q.services.multi-file-challenge-service-smartide', [
    codemirrorWorkspaceServiceModule,
    storageServiceModule,
    smartIdeDialogComponentModule,
    trackerServiceModule,
    appAlertComponentModule,
  ])

  .factory(
    'multiFileChallengeServiceSmartIde',
    function (
      $timeout,
      multiFileChallengeServiceDND,
      codemirrorWorkspaceService,
      smartIdeDialog,
      $q,
      $sessionStorage,
      tracker,
      appAlert,
    ) {
      /**
       * Sets up the SmartIDE workspace.
       *
       * @param {*} svc - MultiFileChallengeService
       * @param {*} opts
       * @param {boolean?} opts.editingChallenge - If true, override the challenge's SmartIDE enabled flag
       * @param {Function} workspaceUpdater - Callback whenever workspace is changed
       */
      function setupSmartIde(svc, opts, workspaceUpdater) {
        let workspace;
        let smartIdeEnabled;
        const smartIdeDisabledKey =
          SMART_IDE_DISABLED_PREFERENCE_KEY +
          svc.challenge.id +
          '-' +
          (svc.solution ? svc.solution.id || 'preview' : '');

        /**
         * Re-lays out the file tabs, on the next digest so the workspace change
         * has landed first.
         *
         * @private
         */
        const _refreshFileLayout = () => {
          if (!svc.fileLayout) return;
          $timeout(() => {
            if (svc.fileLayout) {
              svc.fileLayout.refresh();
            }
          });
        };

        /**
         * Creates a workspace or dummy workspace if it's enabled.
         *
         * @param {boolean?} enabled - Allows the user to disable SmartIDE if they don't want it
         * @returns {{}} Workspace Service
         * @private
         */
        const _createWorkspace = (enabled) => {
          const workspace = codemirrorWorkspaceService.createIfEnabled({
            enabled: smartIdeEnabled && enabled,
            solution: svc.solution,
            challenge: svc.challenge,
            initialFilesGetter: () => svc.allFiles,
          });
          const track = (eventName) => {
            const solution = svc.solution || {};
            const challenge = svc.challenge || {};
            const assessment = solution.assessment || {};
            tracker.trackEvent(`SmartIDE ${eventName}`, {
              solutionId: solution.id,
              challengeId: challenge.id,
              assessmentId: assessment.id,
            });
          };
          workspace.on(WORKSPACE_STATUS.CONNECTED, () => {
            track('Started');
            if (svc.solution) {
              svc.solution.usedSmartIde = true;
            }
          });
          workspace.on(WORKSPACE_STATUS.CONNECTION_FAILED, () => {
            track('Failed');
            // The lightbulb tooltip alone is not enough — a candidate can lose
            // an entire timed assessment without ever hovering it.
            appAlert.error(SMART_IDE_CONNECTION_FAILED_MESSAGE, {
              linkTitle: SMART_IDE_RESTART_LINK_TITLE,
              linkAction: () => svc.restartSmartIde(),
            });
          });
          workspace.on(WORKSPACE_STATUS.CONNECTION_ERROR, () => track('Error'));

          workspaceUpdater(workspace);
          return workspace;
        };

        // toolbar action for workspaces
        svc.workspaceStatusAction = {
          id: 'workspace-status',
          get icon() {
            let baseIcon = 'lightbulb';
            const { status } = workspace;
            if (status === WORKSPACE_STATUS.CONNECTED) {
              baseIcon += '-on';
            } else if (
              [
                WORKSPACE_STATUS.DISABLED,
                WORKSPACE_STATUS.DESTROYING,
                WORKSPACE_STATUS.CONNECTION_FAILED,
              ].includes(status)
            ) {
              baseIcon += '-disabled';
            }
            return `${baseIcon} workspace-status-${status}`;
          },
          get toolbarTooltip() {
            return WORKSPACE_STATUS_LABELS[workspace.status];
          },
          onToolbarClick: () => {
            smartIdeDialog.open(svc);
          },
          get hidden() {
            return !smartIdeEnabled || !workspace.status;
          },
        };

        /**
         * Allow the user to turn SmartIDE on or off. Saves the user's preference.
         * @param {boolean?} enabled - If provided, sets SmartIDE to that state. Otherwise, toggles the current state.
         * @returns {Promise} When resolved, SmartIDE will either be destroyed, or have begun setting up.
         */
        svc.toggleSmartIde = (enabled = !workspace.enabled) => {
          // we store whether the user wants it disabled;
          if (enabled) {
            delete $sessionStorage[smartIdeDisabledKey];
          } else {
            $sessionStorage[smartIdeDisabledKey] = true;
          }

          let promise = $q.resolve();

          if (!enabled && workspace.enabled) {
            // wait until we destroy the service
            promise = workspace.destroy().then(() => {
              // then set up the dummy service
              svc.workspace = workspace = _createWorkspace(false);
            });
          } else if (enabled && !workspace.enabled) {
            // immediately set up the full service (if available)
            svc.workspace = workspace = _createWorkspace(true);
          }
          return promise.finally(_refreshFileLayout);
        };

        /**
         * Tears SmartIDE down and brings it back up. Shared by the SmartIDE
         * dialog and the connection-failure alert so there is one restart path.
         *
         * @returns {Promise} Resolves once SmartIDE has begun setting up again
         */
        svc.restartSmartIde = () =>
          svc.toggleSmartIde(false).then(() => svc.toggleSmartIde(true));

        /**
         * Everything that decides which language server pod a workspace needs.
         * Two calls with the same signature would build an identical workspace.
         *
         * @returns {string} Comparable signature of the current configuration
         * @private
         */
        const _workspaceSignature = () => {
          const runConfig = (svc.challenge && svc.challenge.runConfig) || {};
          // Stringified rather than joined so the language server and service
          // lists compare as lists, and so an absent value stays distinct from
          // an empty one.
          return angular.toJson([
            svc.challenge && svc.challenge.id,
            svc.solution && svc.solution.id,
            runConfig.language,
            runConfig.languageVersion,
            runConfig.preset,
            runConfig.languageServers,
            runConfig.services,
            svc.challenge &&
              svc.challenge.smartIdeEnabled(opts.editingChallenge),
            !!$sessionStorage[smartIdeDisabledKey],
          ]);
        };

        /**
         * The file contents the pod was last seeded with, so a re-seed only
         * happens when a file actually moved.
         *
         * @returns {string} Comparable digest of every file's path and contents
         * @private
         */
        const _filesDigest = () =>
          angular.toJson(
            svc.allFiles.map((file) => [file.path, file.contents]),
          );

        // Statuses a workspace cannot come back from, so reconfiguring has to
        // build a new one rather than hand this one back to the editors.
        const DEAD_STATUSES = [
          WORKSPACE_STATUS.CONNECTION_FAILED,
          WORKSPACE_STATUS.DESTROYING,
        ];
        const _workspaceIsLive = () =>
          !!svc.workspace &&
          svc.workspace.enabled &&
          !DEAD_STATUSES.includes(svc.workspace.status);

        let lastWorkspaceSignature = null;
        let lastFilesDigest = null;

        /**
         * Configures and enables or disable SmartIDE, should be called whenever the language changes
         */
        svc.reconfigureSmartIde = () => {
          // Callers fire this on any solution re-init, not just a language
          // change — including a change-language dialog the candidate opened
          // and then dismissed. Rebuilding there threw away a working pod and
          // cost ~20s of cold start for no change at all (QUAL-2250), so keep
          // the workspace we have when nothing it depends on has moved.
          const signature = _workspaceSignature();

          if (signature === lastWorkspaceSignature && _workspaceIsLive()) {
            svc.updateFilePaths();
            // A code reset reaches us by this same path, and replaces the
            // solution's files wholesale without telling the pod. Re-seeding is
            // one request against a warm pod rather than a rebuild — but only
            // when something actually moved, so a dismissed dialog costs
            // nothing and never re-triggers diagnostics under the cursor.
            const filesDigest = _filesDigest();
            if (filesDigest !== lastFilesDigest) {
              lastFilesDigest = filesDigest;
              svc.workspace.initializeFiles(svc.allFiles);
            }
            _refreshFileLayout();
            return;
          }
          lastWorkspaceSignature = signature;

          if (svc.workspace) {
            svc.workspace.destroy();
            svc.workspace = null;
          }

          svc.updateFilePaths();

          smartIdeEnabled = svc.challenge.smartIdeEnabled(
            opts.editingChallenge,
          );

          workspace = _createWorkspace(!$sessionStorage[smartIdeDisabledKey]);
          // A new workspace seeds the pod from `initialFilesGetter` itself, so
          // these files are already on it.
          lastFilesDigest = _filesDigest();

          if (smartIdeEnabled) {
            svc.workspace = workspace;
          }
          _refreshFileLayout();
        };

        // perform initial SmartIDE configuration
        svc.reconfigureSmartIde();
      }

      return {
        setupSmartIde,
      };
    },
  );
